"use client";

import { useCallback, useRef, useState, type SetStateAction } from "react";
import toast from "react-hot-toast";
import { useCookie } from "@/hooks/useCookie";
import useWebSocket from "@/hooks/useWebSocket";
import { usePayment } from "@/stores/appStore";
import { buildImageGenerationWsUrl } from "@/lib/websocket/urls";
import { trackResearchDrawingTask } from "@/lib/task-notifications";
import type { ImageGenerationRequest, ImageGenerationWsMessage, ImageType } from "./types";

type GenerationPhase = "idle" | "connecting" | "created" | "generating" | "saving" | "done" | "error";

export type GenerationState = {
  phase: GenerationPhase;
  taskId?: number;
  imageUrl?: string;
  beansCost?: number;
  imageType?: ImageType | string;
  researchDirection?: string;
  message: string;
  error?: {
    code?: number;
    type?: string;
  };
};

const initialState: GenerationState = {
  phase: "idle",
  message: "",
};

const DAILY_LIMIT_ERROR_COPY = "今日科研绘图生成次数已达上限：基础用户 3 次/日，Plus 会员 100 次/日，Plus 3倍会员 300 次/日。";
const DAILY_LIMIT_ERROR_CODES = new Set([5002, 5101]);
const DAILY_LIMIT_ERROR_TYPES = new Set([
  "daily_limit_exceeded",
  "image_generation_daily_limit_exceeded",
  "research_drawing_daily_limit_exceeded",
]);

function isDailyLimitError(code?: number, errorType?: string) {
  return (typeof code === "number" && DAILY_LIMIT_ERROR_CODES.has(code))
    || (typeof errorType === "string" && DAILY_LIMIT_ERROR_TYPES.has(errorType));
}

function normalizeErrorCode(value: unknown) {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const code = typeof value === "number" ? value : Number(value);
  return Number.isFinite(code) ? code : undefined;
}

function normalizeMessage(data: any): ImageGenerationWsMessage | null {
  if (!data || typeof data !== "object") {
    return null;
  }

  // Image Generation Agent follows the direct message shape:
  // { type: "task_created" | "task_status" | "task_result" | "error" | "done", data: ... }
  if (["task_created", "task_status", "task_result", "error", "done"].includes(data.type)) {
    return data as ImageGenerationWsMessage;
  }

  if (data.type === "ping") {
    return null;
  }

  if (data.scene !== "image_gen") {
    return null;
  }

  const event = data.event || data.type;
  if (event === "task_created") {
    return {
      type: "task_created",
      data: {
        taskId: Number(data.taskId ?? data.task_id ?? data.id),
        status: "created",
        beansCost: Number(data.beansCost ?? data.beans_cost ?? 0),
        message: data.message || "任务创建成功，准备生成图片",
      },
    };
  }

  if (event === "generating" || event === "saving" || event === "task_status") {
    const status = (data.status || event) as "generating" | "saving";
    return {
      type: "task_status",
      data: {
        taskId: Number(data.taskId ?? data.task_id ?? data.id),
        status,
        message: data.message || (status === "saving" ? "正在保存图片..." : "正在生成图片..."),
      },
    };
  }

  if (event === "task_result" || event === "done" || event === "complete") {
    return {
      type: "task_result",
      data: {
        taskId: Number(data.taskId ?? data.task_id ?? data.id),
        imageUrl: data.imageUrl ?? data.image_url,
        imageSize: data.imageSize ?? data.image_size,
        aspectRatio: data.aspectRatio ?? data.aspect_ratio,
        beansCost: Number(data.beansCost ?? data.beans_cost ?? 0),
        imageType: data.imageType ?? data.image_type,
        researchDirection: data.researchDirection ?? data.research_direction,
      },
    };
  }

  if (event === "error" || event === "failed") {
    return {
      type: "error",
      data: {
        taskId: data.taskId ? Number(data.taskId) : data.task_id ? Number(data.task_id) : undefined,
        code: normalizeErrorCode(data.code),
        errorType: data.errorType ?? data.error_type ?? data.reason,
        message: data.message || "图片生成失败",
      },
    };
  }

  return null;
}

export function useImageGenerationSocket() {
  const [state, setState] = useState<GenerationState>(initialState);
  const stateRef = useRef<GenerationState>(initialState);
  const pendingTaskIdRef = useRef<number | undefined>(undefined);
  const ignoredTaskIdsRef = useRef(new Set<number>());
  const acceptsMessagesRef = useRef(false);
  const sessId = useCookie("FDSID");
  const { openPaymentRecharge } = usePayment();

  const setTrackedState = useCallback((next: SetStateAction<GenerationState>) => {
    setState((previous) => {
      const resolved = typeof next === "function"
        ? (next as (value: GenerationState) => GenerationState)(previous)
        : next;
      stateRef.current = resolved;
      return resolved;
    });
  }, []);

  const handleMessage = useCallback((data: any) => {
    const message = normalizeMessage(data);
    if (!message) return;

    if (!acceptsMessagesRef.current) {
      return;
    }

    const messageTaskId = "data" in message && message.data?.taskId
      ? Number(message.data.taskId)
      : undefined;
    if (messageTaskId && ignoredTaskIdsRef.current.has(messageTaskId)) {
      return;
    }

    if (
      pendingTaskIdRef.current &&
      messageTaskId
    ) {
      if (messageTaskId !== Number(pendingTaskIdRef.current)) {
        return;
      }
    }

    if (message.type === "task_created") {
      pendingTaskIdRef.current = message.data.taskId;
      trackResearchDrawingTask(message.data.taskId);
      setTrackedState({
        phase: "created",
        taskId: message.data.taskId,
        beansCost: message.data.beansCost,
        message: message.data.message || "任务创建成功，准备生成图片",
      });
      return;
    }

    if (message.type === "task_status") {
      pendingTaskIdRef.current ??= message.data.taskId;
      trackResearchDrawingTask(message.data.taskId);
      setTrackedState((prev) => ({
        ...prev,
        phase: message.data.status,
        taskId: message.data.taskId,
        message: message.data.message,
      }));
      return;
    }

    if (message.type === "task_result") {
      pendingTaskIdRef.current = undefined;
      acceptsMessagesRef.current = false;
      setTrackedState((prev) => ({
        ...prev,
        phase: "done",
        taskId: message.data.taskId,
        imageUrl: message.data.imageUrl,
        beansCost: message.data.beansCost,
        imageType: message.data.imageType ?? (message.data as any).image_type ?? prev.imageType,
        researchDirection: message.data.researchDirection,
        message: "图片生成完成",
      }));
      return;
    }

    if (message.type === "error") {
      pendingTaskIdRef.current = undefined;
      acceptsMessagesRef.current = false;
      if (Number(message.data.code) === 5001) {
        openPaymentRecharge(0);
      }
      const displayMessage = isDailyLimitError(message.data.code, message.data.errorType)
        ? DAILY_LIMIT_ERROR_COPY
        : message.data.message || "图片生成失败";
      setTrackedState((prev) => ({
        ...prev,
        phase: "error",
        taskId: message.data.taskId ?? prev.taskId,
        message: displayMessage,
        error: {
          code: message.data.code,
          type: message.data.errorType,
        },
      }));
      toast.error(displayMessage);
    }
  }, [openPaymentRecharge, setTrackedState]);

  const url = buildImageGenerationWsUrl(sessId);
  const { isConnected, sendMessage } = useWebSocket({
    url,
    onMessage: (event) => {
      try {
        handleMessage(JSON.parse(event.data));
      } catch (error) {
        console.error("Failed to parse image generation WebSocket message:", error, event.data);
      }
    },
    reconnectAttempts: 3,
  });

  const generate = useCallback((request: ImageGenerationRequest) => {
    const activeTaskId = pendingTaskIdRef.current ?? stateRef.current.taskId;
    if (activeTaskId) {
      ignoredTaskIdsRef.current.add(activeTaskId);
    }
    pendingTaskIdRef.current = undefined;
    acceptsMessagesRef.current = isConnected;
    setTrackedState({
      phase: isConnected ? "connecting" : "error",
      imageType: request.imageType,
      message: isConnected ? "正在提交生成任务..." : "网络连接不稳定，请刷新页面后重试",
    });

    if (!isConnected) {
      toast.error("网络连接不稳定，请刷新页面后重试");
      return;
    }

    sendMessage({
      userInput: request.userInput,
      imageModel: request.imageModel,
      imageType: request.imageType,
      imageSize: request.imageSize,
      aspectRatio: request.aspectRatio,
      previousImages: request.previousImages,
      researchDirection: request.researchDirection,
      taskId: request.taskId ?? 0,
    });
  }, [isConnected, sendMessage, setTrackedState]);

  const reset = useCallback(() => {
    const activeTaskId = pendingTaskIdRef.current ?? stateRef.current.taskId;
    if (activeTaskId) {
      ignoredTaskIdsRef.current.add(activeTaskId);
    }
    pendingTaskIdRef.current = undefined;
    acceptsMessagesRef.current = false;
    setTrackedState(initialState);
  }, [setTrackedState]);

  const resume = useCallback((taskId: number) => {
    ignoredTaskIdsRef.current.delete(taskId);
    pendingTaskIdRef.current = taskId;
    acceptsMessagesRef.current = true;
    setTrackedState((previous) => (
      previous.taskId === taskId && ["created", "generating", "saving"].includes(previous.phase)
        ? previous
        : {
          phase: "generating",
          taskId,
          message: "图片生成中",
        }
    ));
  }, [setTrackedState]);

  return {
    state,
    isConnected,
    isGenerating: ["connecting", "created", "generating", "saving"].includes(state.phase),
    generate,
    reset,
    resume,
  };
}
